import {
	CloseOutlined,
	FileTextOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { napsaIntegrationServiceApi } from 'Api/napsaIntegration';
import { Modal, Table, Tag, Tooltip } from 'antd';
import Column from 'antd/es/table/Column';
import Title from 'antd/es/typography/Title';
import Buttons from 'components/Global/Buttons';
import Ellipse from 'components/Global/Ellipse';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
	Paid,
	PaymentUnderProcessWithText,
	PendingForApproval,
	RejectedByAccounts,
} from 'utils/svgs';
import { formatNumber, invalidText, toastText } from 'utils/utils';
import ReportPopOver from '../../ReportPopOver';
import styles from './index.module.scss';
import { useSelector } from 'react-redux';
import NapsaLogHeader from '../Header';
import './index.scss';

import useDebounce from 'components/Global/Hooks/UseDebounce';

type tableProps = {
	openUploadRecordModal: (napsaRecordId: string) => void;
	calculateNapsaCurrentPage: number;
	calculateNapsaPageSize: number;
	calculateNapsaTotalRecords: number;
	calculateNapsaIsLoading: boolean;
	calculateNapsaData: any;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	fetchNapsaDetails: any;
};

const TableComponent = (props: tableProps) => {
	const {
		openUploadRecordModal,
		calculateNapsaCurrentPage,
		calculateNapsaPageSize,
		calculateNapsaTotalRecords,
		calculateNapsaIsLoading,
		calculateNapsaData,
		tableChangeHandler,
		fetchNapsaDetails,
	} = props;

	const [statusLoading, setStatusLoading] = useState(false);

	const [searchValue, setSearchValue] = useState('');
	const [statusFilterValue, setStatusFilterValue] = useState('');
	const [returnLogsModal, setReturnLogModal] = useState<boolean>(false);
	const [modalData, setModalData] = useState<any>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(30);
	const [totalRecords, setTotalRecords] = useState(10);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');
	const [recordId, setRecordId] = useState<string>('');
	const [returnLogsLoading, setReturnLogsLoading] = useState<{
		[key: string]: boolean;
	}>({});
	const [checkStatusLoading, setCheckStatusLoading] = useState<{
		[key: string]: boolean;
	}>({});

	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	const handleSearch = (value: string) => {
		setSearchValue(value);
	};

	const debounceSearch = useDebounce(searchValue, 500);

	const handleSearchApi = async (value: string) => {
		//search api
	};
	const handleStatusFilter = (value: string) => {
		setStatusFilterValue(value);
	};
	const checkSubmitReturnStatus = async (napsaRecordId: string) => {
		try {
			setCheckStatusLoading((prev) => ({
				...prev,
				[napsaRecordId]: true,
			}));
			await napsaIntegrationServiceApi.checkReturnStatus({
				napsaRecordId,
			});
			await fetchNapsaDetails();
			toastText('Status checked successfully', 'success');
		} catch (error: any) {
			const errorMessage =
				error.response.data.message ||
				'Something went wrong in checking the status of Return';
			toastText(errorMessage, 'error');
		} finally {
			setCheckStatusLoading((prev) => ({
				...prev,
				[napsaRecordId]: false,
			}));
		}
	};

	const closeConfirmationModal = () => {
		setReturnLogModal(false);
		setRecordId('');
	};

	const openConfirmationModal = async (selectedRecord: string) => {
		try {
			setReturnLogsLoading((prev) => ({
				...prev,
				[selectedRecord]: true,
			}));
			setReturnLogModal(true);
			const query = {
				page: currentPage,
				sortBy: sortField,
				sortOrder: sortOrder,
				pageSize: pageSize,
				napsaRecordId: selectedRecord,
				searchText: debounceSearch,
				status: statusFilterValue,
			};
			const response =
				await napsaIntegrationServiceApi.getNapsaReturnLogs(query);
			if (response && response.data) {
				setTotalRecords(response.data.total);
				setModalData(response.data.data);
			} else {
				toastText('No return logs found', 'error');
				setModalData([]); // Set to empty array if no data
			}
		} catch (error: any) {
			const errorMessage =
				error.response.data.message ||
				'Something went wrong in fetching the Return Logs';
			toastText(errorMessage, 'error');
		} finally {
			setReturnLogsLoading((prev) => ({
				...prev,
				[selectedRecord]: false,
			}));
		}
	};
	const tableChangeHandlerForLogs = (
		pagination: any,
		filters: any,
		sorter: any
	) => {
		setTotalRecords(pagination.total);
		setCurrentPage(pagination.current);
		setPageSize(pagination.pageSize);
		setSortOrder(
			sorter?.order === 'ascend' ? 'asc' : sorter?.order ? 'desc' : ''
		);
		setSortField(sorter.field);
	};

	useEffect(() => {
		if (!invalidText(recordId)) {
			openConfirmationModal(recordId);
		}
	}, [
		currentPage,
		pageSize,
		sortField,
		sortOrder,
		recordId,
		debounceSearch,
		statusFilterValue,
	]);
	return (
		<>
			<Table
				dataSource={calculateNapsaData}
				rowKey={(record: any) => record.id}
				loading={calculateNapsaIsLoading}
				pagination={{
					total: calculateNapsaTotalRecords,
					current: calculateNapsaCurrentPage,
					pageSize: calculateNapsaPageSize,
					showSizeChanger: false,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				scroll={{ y: 'calc(100vh - 340px)' }}
				onChange={tableChangeHandler}
			>
				<Column
					title="Created Date"
					dataIndex="createdAt"
					key="createdAt"
					render={(text: any) => dayjs(text).format('DD/MM/YYYY')}
					sorter={true}
					width={'13%'}
					className="bg-white"
				/>
				<Column
					title="Currency"
					dataIndex="currency"
					key="currency"
					width={'10%'}
					className="bg-white"
				/>
				<Column
					title="Pay Period"
					dataIndex="payPeriodName"
					key="payPeriodName"
					width={'15%'}
					className="bg-white"
					render={(text) => (
						<Ellipse
							message={text}
							maxLength={20}
							key={text}
							tooltipMessage={text}
							isTooltip={true}
						/>
					)}
				/>
				<Column
					title="Calculated by"
					dataIndex="calculatedBy"
					key="calculatedBy"
					render={(text: any) => text}
					width={'12%'}
					className="bg-white"
				/>
				<Column
					title="Reports"
					dataIndex="NapsaReport"
					key="NapsaReport"
					className="bg-white"
					width={'15%'}
					render={(_: any, record: any) => (
						<ReportPopOver
							content={record.NapsaReport}
							reportType="NAPSA"
						/>
					)}
				/>
				<Column
					title="Upload Returns"
					dataIndex="NapsaReport"
					key="NapsaReport"
					className="bg-white"
					width={'11%'}
					render={(_: any, record: any) => (
						<div
							style={{
								display: 'flex',
								gap: '2rem',
								alignItems: 'center',
							}}
						>
							{!record.isCheckStatus ? (
								<UploadOutlined
									style={{
										fontSize: 18,
										color: statusLoading
											? '#ccc'
											: '#584495',
										cursor: statusLoading
											? 'not-allowed'
											: 'pointer',
									}}
									onClick={() =>
										!statusLoading &&
										openUploadRecordModal(record.id)
									}
								/>
							) : (
								<Tooltip title="The return can only be submitted to NAPSA after the status has been checked.">
									<div className="text-gray-400">N/A</div>
								</Tooltip>
							)}
						</div>
					)}
				/>

				<Column
					title="Check Returns status"
					dataIndex="logExists"
					key="logExists"
					className="bg-white"
					width={'12%'}
					render={(_, record: any) => {
						return record.isCheckStatus ? (
							<div
								style={{
									display: 'flex',
									gap: '2rem',
									alignItems: 'center',
								}}
							>
								<Buttons
									buttons={[
										{
											text: 'Check Status',
											isLoading:
												checkStatusLoading[record.id],
											className: 'btn-blue',
											isSubmit: false,
											disabled: false,
											onclick: () => {
												checkSubmitReturnStatus(
													record.id
												);
											},
											fontSize: '1.5rem',
											minWidth: '7rem',
											minHeight: '2rem',
										},
									]}
								/>
							</div>
						) : (
							<Tooltip title="Status information is only available for the records that have been submitted to NAPSA.">
								<div className="text-gray-400">N/A</div>
							</Tooltip>
						);
					}}
				/>
				<Column
					title="Return Logs"
					dataIndex=""
					key=""
					className="bg-white"
					width={'20%'}
					render={(_, record: any) => {
						return record.logExists ? (
							<div
								style={{
									display: 'flex',
									gap: '2rem',
									alignItems: 'center',
								}}
							>
								<FileTextOutlined
									style={{
										fontSize: 18,
										color: '#594495',
										cursor: 'pointer',
									}}
									onClick={() => setRecordId(record.id)}
								/>
							</div>
						) : (
							<Tooltip title="Return logs are only available for the records that have been submitted to NAPSA.">
								<div className="text-gray-400">N/A</div>
							</Tooltip>
						);
					}}
				/>
			</Table>
			<Modal
				open={returnLogsModal}
				onCancel={closeConfirmationModal}
				footer={null}
				closable={false}
				width={1300}
				style={{
					borderRadius: '10px',
				}}
				className="confirmation-modal"
				centered
			>
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<Title level={4}>
							Employee Details
							<span
								style={{
									color: '#584495',
								}}
							></span>
						</Title>
						<div
							className={styles['close-icon']}
							onClick={closeConfirmationModal}
						>
							<CloseOutlined />
						</div>
					</div>

					<>
						<div className={styles['modal-body']}>
							<NapsaLogHeader
								searchText={searchValue}
								handleSearch={handleSearch}
								handleSearchApi={handleSearchApi}
								StatusFilter={statusFilterValue}
								handleStatusFilter={handleStatusFilter}
							/>
							<div className="return-log-table">
								<Table
									dataSource={modalData}
									rowKey={(record) => record.id}
									loading={returnLogsLoading[recordId]}
									scroll={{ x: 'max-content', y: 400 }}
									pagination={{
										total: totalRecords,
										current: currentPage,
										pageSize: pageSize,
										showSizeChanger: false,
										pageSizeOptions: [10, 20, 50, 100, 200],
									}}
									onChange={tableChangeHandlerForLogs}
								>
									<Column
										title="Employee Name"
										dataIndex="firstName"
										key="firstName"
										className="bg-white"
										width={200}
										fixed="left"
										render={(_, record: any) =>
											`${record.employeeName}`
										}
									/>
									<Column
										title="SSN"
										dataIndex="employeeSsn"
										key="employeeSsn"
										className="bg-white"
										width={120}
										render={(_, record: any) =>
											record.employeeSsn || '-'
										}
									/>
									<Column
										title="NRC"
										dataIndex="employeeNrc"
										key="employeeNrc"
										className="bg-white"
										width={120}
										render={(_, record: any) =>
											record.employeeNrc || '-'
										}
									/>
									<Column
										title="DOB"
										dataIndex="employeeDob"
										key="employeeDob"
										className="bg-white"
										width={120}
										render={(_, record: any) =>
											dayjs(record.dateOfBirth).format(
												'DD/MM/YYYY'
											) || '-'
										}
									/>
									<Column
										title="Employee Share"
										dataIndex="employeeShare"
										key="employeeShare"
										className="bg-white"
										width={150}
										render={(record) =>
											formatNumber(
												record,
												profileData.CompanyCurrencies,
												false
											) || '-'
										}
									/>
									<Column
										title="Gross Pay"
										dataIndex="grossPay"
										key="grossPay"
										className="bg-white"
										width={150}
										render={(record) =>
											formatNumber(
												record,
												profileData.CompanyCurrencies,
												false
											) || '-'
										}
									/>
									<Column
										title="Status"
										dataIndex="status"
										key="status"
										width={150}
										className="bg-white"
										render={(value, record) => {
											let statusText = '';
											let statusClass = '';
											let StatusIcon = null;

											switch (value) {
												case 'PENDING':
													statusText = 'Pending';
													statusClass =
														styles[
															'status-pending'
														];
													StatusIcon = (
														<PendingForApproval />
													);
													break;
												case 'FAILED':
													statusText = 'Failed';
													statusClass =
														styles[
															'status-rejected'
														];
													StatusIcon = (
														<RejectedByAccounts />
													);
													break;
												case 'SUCCESS':
													statusText = 'Success';
													statusClass =
														styles[
															'status-success'
														];
													StatusIcon = <Paid />;
													break;
												case 'PROCESSING':
													statusClass =
														styles[
															'status-processing'
														];
													StatusIcon = (
														<PaymentUnderProcessWithText />
													);
													break;
												default:
													statusText =
														'Unknown Status';
													statusClass =
														styles[
															'status-unknown'
														];
													StatusIcon = null;
											}

											return (
												<div
													className={
														styles[
															'statusContainer'
														]
													}
												>
													<div
														className={
															styles[
																'statusDisplay'
															]
														}
													>
														<Tag
															className={`${styles['statusTag']} ${statusClass}`}
															bordered={false}
														>
															<span
																className={
																	styles[
																		'statusIcon'
																	]
																}
															>
																{StatusIcon}
															</span>
															<span>
																{statusText}
															</span>
														</Tag>
													</div>
												</div>
											);
										}}
									/>
									<Column
										title="Api Response"
										dataIndex="apiResponse"
										key="apiResponse"
										width={200}
										className="bg-white"
										render={(_, record: any) => {
											const response = record.ApiResponse;
											return response &&
												response.length > 0 ? (
												<Ellipse
													maxLength={20}
													message={response[0]}
													isTooltip={true}
													tooltipMessage={response[0]}
													key={response[0]}
												/>
											) : (
												'-'
											);
										}}
									/>
								</Table>
							</div>
						</div>
					</>
				</div>
			</Modal>
		</>
	);
};

export default TableComponent;
